import type { Infer } from "convex/values";
import type { configReferentielValidator } from "@convex/schema";
import { HEXACO_FACTORS, MOTIVATION_DIMS } from "@repo/pami";
import {
  estAttente,
  intervalleDeAttente,
  type AttenteDimension,
  type Echelle,
} from "@convex/referentiel/fourchette";

export type ConfigReferentiel = Infer<typeof configReferentielValidator>;
export type Dimension = ConfigReferentiel["dimensions"][number];

/** Réponse transmise : l'attente, jamais des bornes. Le serveur en dérive
 *  l'intervalle cible — le client n'est pas source de vérité du calcul. */
export type ReponseFourchette = {
  code: string;
  attente?: AttenteDimension;
};

export type ValeurDimension = {
  attente?: AttenteDimension;
};

export type Etat = Record<string, ValeurDimension>;

// Titres des sections (§6.3) : les attendus de personnalité sont introduits à
// part. Un référent qui enchaîne 20 dimensions identiques ne distingue pas ce
// qu'il est en train de faire.
export const SECTIONS: Record<string, { titre: string; introduction?: string }> = {
  motivation: { titre: "Attendus de motivation pour le poste" },
  personnalite: {
    titre: "Attendus de personnalité pour le poste",
    introduction:
      "Ces attendus décrivent des tendances de comportement observées dans le poste. Un écart n'invalide pas une candidature : il signale un point à explorer en entretien.",
  },
};

/** Ce que mesure un trait PAMI, et ce que disent un score bas et un score
 *  élevé : les mêmes textes que le rapport candidat. */
export type DefinitionTrait = {
  definition: string;
  bas: string;
  haut: string;
};

/** Définitions indexées par code de dimension, c'est-à-dire par PRÉFIXE de
 *  trait (`prefix`, la clé du score — pas `code`, qui diffère pour les
 *  facettes HEXACO). */
const DEFINITIONS_TRAITS = new Map<string, DefinitionTrait>(
  [...MOTIVATION_DIMS, ...HEXACO_FACTORS.flatMap((facteur) => facteur.facets)].map(
    (trait) => [
      trait.prefix,
      {
        definition: trait.description,
        bas: trait.interpretations.bas,
        haut: trait.interpretations.haut,
      },
    ],
  ),
);

/**
 * Définition d'une dimension du référentiel, lue dans le référentiel PAMI par
 * son code plutôt que copiée dans la config : elle vaut aussi pour les
 * référentiels déjà créés. `undefined` pour une dimension hors PAMI.
 */
export function definitionTrait(code: string): DefinitionTrait | undefined {
  return DEFINITIONS_TRAITS.get(code);
}

/** Groupe les dimensions par catégorie EN CONSERVANT l'ordre de la config.
 *  Chaque dimension garde son index global : c'est la position du carrousel. */
export function grouper(
  dimensions: Dimension[],
): [string, { dimension: Dimension; index: number }[]][] {
  const groupes = new Map<string, { dimension: Dimension; index: number }[]>();
  dimensions.forEach((dimension, index) => {
    const cle = dimension.categorie ?? "";
    groupes.set(cle, [...(groupes.get(cle) ?? []), { dimension, index }]);
  });
  return [...groupes.entries()];
}

/**
 * État de départ : VIDE. Aucune valeur par défaut — un « score élevé »
 * prérempli sur 20 dimensions se ferait valider sans être lu, et le référentiel
 * décrirait alors les réglages d'usine plutôt que le poste.
 */
export function etatInitial(config: ConfigReferentiel, brouillon: unknown): Etat {
  const etat: Etat = {};
  for (const dimension of config.dimensions) etat[dimension.code] = {};
  const reponses = (brouillon as { reponses?: ReponseFourchette[] } | null)
    ?.reponses;
  if (Array.isArray(reponses)) {
    for (const reponse of reponses) {
      if (etat[reponse.code] === undefined) continue;
      // Un brouillon d'avant l'attente unique (importance × niveau) repasse
      // « à renseigner » : sa zone ne correspond à aucune des attentes
      // actuelles, la convertir ferait valider un choix jamais fait.
      etat[reponse.code] = estAttente(reponse.attente)
        ? { attente: reponse.attente }
        : {};
    }
  }
  return etat;
}

export function versReponses(etat: Etat): ReponseFourchette[] {
  return Object.entries(etat).map(([code, valeur]) => ({ code, ...valeur }));
}

export const estComplete = (valeur: ValeurDimension | undefined) =>
  valeur?.attente !== undefined;

/** Zone cible de l'attente, ou `undefined` sans attente ou sans zone (« pas
 *  d'attente particulière »). */
export function zoneDe(
  valeur: ValeurDimension,
  echelle: Echelle,
): { min: number; max: number } | undefined {
  if (valeur.attente === undefined || valeur.attente === "aucune")
    return undefined;
  return intervalleDeAttente(valeur.attente, echelle);
}

/**
 * Prochaine position à montrer après `depuis` : la première dimension encore
 * incomplète en continuant vers la fin, puis en reprenant au début ; le
 * récapitulatif (`dimensions.length`) quand tout est renseigné.
 */
export function prochaineIncomplete(
  dimensions: readonly { code: string }[],
  etat: Etat,
  depuis: number,
): number {
  const total = dimensions.length;
  for (let pas = 1; pas <= total; pas++) {
    const index = (depuis + pas) % total;
    if (!estComplete(etat[dimensions[index]!.code])) return index;
  }
  return total;
}
